import {Arrow} from 'components/icons/Arrow';
import Link from 'components/CustomRoutingLink';
import React, {FC, MouseEventHandler, useCallback, useId} from 'react';
import {Typography} from 'components/Typography';
import {IMenuItem} from 'modules/user/types';
import style from './style.module.scss';
import classNames from 'classnames';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {LOGOUT_ROUTE} from 'core/modules/routing/constants';
import {authLogout} from 'modules/auth/thunks';
import Icon from 'components/Icon';
import {usePersonalPrivateRoutes} from 'core/modules/routing/hooks/private/usePersonalPrivateRoutes';
import {useGetPersonalOffersQuery, useGetUserQuery} from 'modules/user/services';
import {TIconName} from 'components/Icon/iconNames';
import {USER_GROUPS_CODE} from 'modules/user/constants';

export interface IProps {
    className?: string;
    menuItemClassName?: string;
    menuList?: IMenuItem[];
    isFullPathMatch?: boolean;
}

export const HeaderUserMenu: FC<IProps> = ({className, menuItemClassName}) => {
    const uid = useId();

    const {data} = useGetUserQuery();
    const {data: personalOffers} = useGetPersonalOffersQuery({});

    const userGroups = data?.groups;

    const isUserEmployee = userGroups?.some((group) => -1 !== USER_GROUPS_CODE.employee.indexOf(group));

    const dispatch = useAppDispatch();

    const handleMenuClick = useCallback<MouseEventHandler<HTMLUListElement>>((event) => {
        event.stopPropagation();
    }, []);

    const handleLogoutClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(
        async (event) => {
            event.preventDefault();
            await dispatch(authLogout);
        },
        [dispatch],
    );

    const {
        favorites: {url: favoritesUrl},
        lk: {url: lk},
        offers: {url: offers},
        history: {url: history},
        settings: {url: settings},
        inviteFriend: {url: inviteFriend},
    } = usePersonalPrivateRoutes();

    const burgerItemsInitial = [
        {
            code: 'personal',
            icon: 'profile',
            text: 'Личный кабинет',
            url: lk(),
        },
        {
            code: 'favorite',
            icon: 'favourite',
            text: 'Избранное',
            url: favoritesUrl(),
        },
        {
            code: 'history',
            icon: 'history',
            text: 'История заказов',
            url: history(),
        },
        {
            code: 'profile-simple',
            icon: 'compass',
            text: 'Профиль SimpleWine',
            url: settings(),
        },
        {
            code: 'exit',
            icon: 'Exit',
            shallow: true,
            text: 'Выйти',
            url: LOGOUT_ROUTE,
        },
    ] as const;

    const burgerItemsWithOffers = [
        ...burgerItemsInitial.slice(0, 2),
        {
            code: 'recs',
            icon: 'shine',
            text: 'Персональные предложения',
            url: offers(),
        },
        ...burgerItemsInitial.slice(2),
    ];

    const burgerItems = personalOffers?.kpAvailable ? burgerItemsWithOffers : burgerItemsInitial;

    return (
        <div className={style.menuContainer}>
            <ul className={classNames(className, style.menuList)} onClick={handleMenuClick}>
                {burgerItems.map((menuItem, index) => {
                    const key = `${menuItem.text}:${index}:${uid}`;
                    return (
                        <Link
                            href={menuItem.url === LOGOUT_ROUTE ? '' : menuItem.url}
                            key={key}
                            legacyBehavior={true}
                            passHref={true}
                        >
                            <a
                                className={classNames(menuItemClassName, style.menuItem)}
                                data-autotest-target={'header-user-auth-dropdn'}
                                data-autotest-target-id={`header-user-auth-dropdn-${menuItem.code}`}
                                onClick={menuItem.url === LOGOUT_ROUTE ? handleLogoutClick : undefined}
                            >
                                {<Icon className={style.menuItemIcon} icon={menuItem.icon as TIconName} />}
                                <Typography className={style.menuItemText} color={'gray80'} element={'p'} variant={'p'}>
                                    {menuItem.text}
                                </Typography>
                            </a>
                        </Link>
                    );
                })}
            </ul>
            {!isUserEmployee && (
                <a
                    className={classNames(style.bonuses, 'user-burger__invite')}
                    data-autotest-target={'header-user-auth-dropdn'}
                    data-autotest-target-id={`header-user-auth-dropdn-ref`}
                    href={inviteFriend()}
                >
                    <Typography className={style.inviteButtonText} variant={'label-16-r'}>
                        Приглашайте друзей и копите бонусы
                    </Typography>
                    <Arrow direction={'right'} type={'chevron'} />
                </a>
            )}
        </div>
    );
};
